export type ParsedManualAddressBlock = {
  address1?: string;
  address2?: string;
  city?: string;
  countryCode?: string;
  email?: string;
  phone?: string;
  postalCode?: string;
  recipientName?: string;
};

const countryLinePattern = /^(?:de|deutschland|germany)$/i;

export function parseManualAddressBlock(value: unknown): ParsedManualAddressBlock {
  const lines = String(value || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const result: ParsedManualAddressBlock = {};
  if (lines.length === 0) return result;

  const remaining = [...lines];
  const emailIndex = remaining.findIndex((line) => /\S+@\S+\.\S+/.test(line));
  if (emailIndex >= 0) {
    result.email = remaining[emailIndex].match(/\S+@\S+\.\S+/)?.[0] || "";
    remaining.splice(emailIndex, 1);
  }

  const phoneIndex = remaining.findIndex((line) => /(?:\+?\d[\d\s()./-]{5,})/.test(line) && !/\b\d{5}\b/.test(line));
  if (phoneIndex >= 0) {
    result.phone = remaining[phoneIndex].match(/(?:\+?\d[\d\s()./-]{5,})/)?.[0] || "";
    remaining.splice(phoneIndex, 1);
  }

  const countryIndex = remaining.findIndex((line) => countryLinePattern.test(line));
  if (countryIndex >= 0) {
    result.countryCode = "DE";
    remaining.splice(countryIndex, 1);
  }

  const postalIndex = remaining.findIndex((line) => /\b\d{5}\b/.test(line));
  if (postalIndex >= 0) {
    const line = remaining[postalIndex];
    const match = line.match(/\b(\d{5})\b\s*(.*)$/) || line.match(/^(.*?)\s*\b(\d{5})\b/);
    if (match) {
      result.postalCode = match[1]?.match(/^\d{5}$/) ? match[1] : match[2] || "";
      result.city = (match[1]?.match(/^\d{5}$/) ? match[2] : match[1] || "")
        .replace(/,?\s*(deutschland|germany|de)$/i, "")
        .trim();
    }
    remaining.splice(postalIndex, 1);
  }

  const streetIndex = remaining.findIndex((line) => /\d/.test(line) || /\b(strasse|straße|str\.|weg|allee|platz|ring|gasse)\b/i.test(line));
  if (streetIndex >= 0) {
    result.address1 = remaining[streetIndex];
    remaining.splice(streetIndex, 1);
  }

  if (remaining.length > 0) result.recipientName = remaining.shift() || "";
  if (remaining.length > 0 && !result.address1) result.address1 = remaining.shift() || "";
  if (remaining.length > 0) result.address2 = remaining.join(" ");
  result.countryCode ||= "DE";
  return result;
}

export function manualAddressBlockIsComplete(parsed: ParsedManualAddressBlock) {
  return Boolean(parsed.recipientName && parsed.address1 && parsed.postalCode && parsed.city);
}
